<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    style="z-index: 1100 !important;"
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props">
        <slot></slot>
      </div>
    </template>

    <v-form
      ref="form"
      @submit.prevent="createClient"
      autocomplete="off"
      data-cy="dialog-createClient-form"
    >
      <v-card v-cloak>
        <v-card-title class="d-flex justify-space-between" data-cy="dialog-createClient-title">
          {{ $t('routes.apiClients.createClient') }}

          <v-btn icon variant="text" @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text scrollable class="pb-0 pt-4 scrollable">
          <v-text-field
            v-model="name"
            :label="$t('routes.apiClients.clientName')"
            :rules="nameRules"
            autocomplete="off"
            required
            variant="solo-filled"
            flat
            data-cy="dialog-createClient-name"
          />

          <v-autocomplete
            v-model="selectedUserId"
            :items="availableUsers"
            :label="$t('routes.apiClients.selectUser')"
            :rules="userRules"
            :loading="loadingUsers"
            item-title="displayName"
            item-value="id"
            variant="solo-filled"
            flat
            clearable
            data-cy="dialog-createClient-user"
          >
            <template v-slot:item="{ props, internalItem }">
              <v-list-item v-bind="props">
                <template v-slot:subtitle>
                  {{ internalItem.raw.email }}
                </template>
              </v-list-item>
            </template>
          </v-autocomplete>

          <v-alert
            v-if="selectedUserHasClient"
            type="warning"
            variant="tonal"
            class="mb-4"
          >
            {{ $t('routes.apiClients.userAlreadyHasClient') }}
          </v-alert>

          <!-- Scopes Selection -->
          <div class="mb-4">
            <div class="text-subtitle-2 mb-2">{{ $t('routes.apiClients.scopes') }} *</div>

            <!-- Files Scopes -->
            <div class="text-caption text-medium-emphasis mb-1">{{ $t('routes.apiClients.scopeFiles') }}</div>
            <div class="d-flex align-center flex-wrap mb-2">
              <v-switch
                v-model="filesFullAccess"
                :label="filesFullAccess ? $t('routes.apiClients.scopeFullAccess') : $t('routes.apiClients.scopeIndividual')"
                density="compact"
                hide-details
                color="primary"
                class="mr-4"
                data-cy="scope-files-switch"
              />
              <template v-if="!filesFullAccess">
                <v-checkbox
                  v-model="filesRead"
                  :label="$t('routes.apiClients.scopeRead')"
                  density="compact"
                  hide-details
                  class="mr-4"
                  data-cy="scope-files-read"
                />
                <v-checkbox
                  v-model="filesWrite"
                  :label="$t('routes.apiClients.scopeWrite')"
                  density="compact"
                  hide-details
                  data-cy="scope-files-write"
                />
              </template>
            </div>

            <!-- Folders Scopes -->
            <div class="text-caption text-medium-emphasis mb-1">{{ $t('routes.apiClients.scopeFolders') }}</div>
            <div class="d-flex align-center flex-wrap mb-2">
              <v-switch
                v-model="foldersFullAccess"
                :label="foldersFullAccess ? $t('routes.apiClients.scopeFullAccess') : $t('routes.apiClients.scopeIndividual')"
                density="compact"
                hide-details
                color="primary"
                class="mr-4"
                data-cy="scope-folders-switch"
              />
              <template v-if="!foldersFullAccess">
                <v-checkbox
                  v-model="foldersRead"
                  :label="$t('routes.apiClients.scopeRead')"
                  density="compact"
                  hide-details
                  class="mr-4"
                  data-cy="scope-folders-read"
                />
                <v-checkbox
                  v-model="foldersWrite"
                  :label="$t('routes.apiClients.scopeWrite')"
                  density="compact"
                  hide-details
                  data-cy="scope-folders-write"
                />
              </template>
            </div>

            <!-- Links Scopes -->
            <div class="text-caption text-medium-emphasis mb-1">{{ $t('routes.apiClients.scopeLinks') }}</div>
            <div class="d-flex align-center flex-wrap mb-2">
              <v-switch
                v-model="linksFullAccess"
                :label="linksFullAccess ? $t('routes.apiClients.scopeFullAccess') : $t('routes.apiClients.scopeIndividual')"
                density="compact"
                hide-details
                color="primary"
                class="mr-4"
                data-cy="scope-links-switch"
              />
              <template v-if="!linksFullAccess">
                <v-checkbox
                  v-model="linksRead"
                  :label="$t('routes.apiClients.scopeRead')"
                  density="compact"
                  hide-details
                  class="mr-4"
                  data-cy="scope-links-read"
                />
                <v-checkbox
                  v-model="linksWrite"
                  :label="$t('routes.apiClients.scopeWrite')"
                  density="compact"
                  hide-details
                  data-cy="scope-links-write"
                />
              </template>
            </div>

            <div v-if="scopeError" class="text-error text-caption">
              {{ $t('routes.apiClients.scopesRequired') }}
            </div>
          </div>

          <div class="bg-error" v-if="errorMessage">
            {{ errorMessage }}
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
            type="submit"
            class="bg-primary"
            :disabled="isSubmitDisabled || isFormSubmitted"
            elevation="0"
            data-cy="dialog-createClient-submit-btn"
          >
            {{ $t('routes.apiClients.create') }}
          </v-btn>
          <v-btn
            variant="text"
            :disabled="isFormSubmitted"
            @click="dialog = false"
            data-cy="dialog-createClient-cancel"
          >
            {{ $t('common.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js'
import { useUserStore } from '@/stores/UserStore.js'
import { useApiClientStore } from '@/stores/ApiClientStore.js'
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';

export default {
  mixins: [DialogActionKeys],
  name: "CreateClientDialog",
  emits: ['clientCreated'],
  data() {
    return {
      dialog: false,
      errorMessage: '',
      loadingUsers: false,

      name: '',
      nameRules: [
        v => !!v || this.$t('routes.apiClients.nameRequired'),
        v => (v && v.length <= 255) || this.$t('routes.apiClients.nameTooLong'),
      ],

      selectedUserId: null,
      userRules: [
        v => !!v || this.$t('routes.apiClients.userRequired'),
      ],

      // Scope switches: true = Full Access, false = Individual permissions
      filesFullAccess: true,
      foldersFullAccess: true,
      linksFullAccess: true,

      // Individual permission selections (used when fullAccess = false)
      filesRead: false,
      filesWrite: false,
      foldersRead: false,
      foldersWrite: false,
      linksRead: false,
      linksWrite: false,

      scopeError: false,

      isFormSubmitted: false,
    };
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.resetForm();
        this.loadUsers();
      }
    },
  },
  methods: {
    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
      this.errorMessage = '';
      this.name = '';
      this.selectedUserId = null;
      // Reset scopes to defaults (Full Access)
      this.filesFullAccess = true;
      this.foldersFullAccess = true;
      this.linksFullAccess = true;
      this.filesRead = false;
      this.filesWrite = false;
      this.foldersRead = false;
      this.foldersWrite = false;
      this.linksRead = false;
      this.linksWrite = false;
      this.scopeError = false;
      this.isFormSubmitted = false;
    },
    async loadUsers() {
      this.loadingUsers = true;
      try {
        const userStore = useUserStore();
        await userStore.refreshUsers({ accessPointId: this.accessPointId });
      } catch (error) {
        console.error('Error loading users', error);
      } finally {
        this.loadingUsers = false;
      }
    },
    async createClient() {
      if (this.isFormSubmitted) {
        return;
      }

      this.errorMessage = '';
      this.scopeError = false;

      const { valid: validForm } = await this.$refs.form.validate();
      if (!validForm) {
        return;
      }

      // Validate scopes (at least one must be selected)
      if (this.selectedScopes.length === 0) {
        this.scopeError = true;
        return;
      }

      if (this.selectedUserHasClient) {
        this.errorMessage = this.$t('routes.apiClients.userAlreadyHasClient');
        return;
      }

      this.isFormSubmitted = true;

      try {
        const apiClientStore = useApiClientStore();
        const client = await apiClientStore.createClient({
          name: this.name,
          userId: this.selectedUserId,
          nodeId: this.accessPointId,
          scopes: this.selectedScopes,
        });

        this.dialog = false;
        this.$emit('clientCreated', client);
      } catch (error) {
        if (error?.response?.data?.error) {
          const apiError = error.response.data.error;
          this.errorMessage = apiError.message || this.$t('routes.apiClients.createError');
        } else {
          this.errorMessage = this.$t('routes.apiClients.createError');
          console.error(error);
        }
      } finally {
        this.isFormSubmitted = false;
      }
    },
  },
  computed: {
    users() {
      const userStore = useUserStore();
      return userStore.getUsers;
    },
    existingClients() {
      const apiClientStore = useApiClientStore();
      return apiClientStore.getClients;
    },
    availableUsers() {
      // Map users to display format
      return this.users.map(user => ({
        id: user.id,
        email: user.email,
        displayName: `${user.firstname || ''} ${user.lastname || ''}`.trim() || user.email,
        hasClient: this.existingClients.some(client => client.user_id === user.id),
      }));
    },
    selectedUserHasClient() {
      if (!this.selectedUserId) return false;
      return this.existingClients.some(client => client.user_id === this.selectedUserId);
    },
    selectedScopes() {
      const scopes = [];

      // Files scopes
      if (this.filesFullAccess) {
        scopes.push('files:full');
      } else {
        if (this.filesRead) scopes.push('files:read');
        if (this.filesWrite) scopes.push('files:write');
      }

      // Folders scopes
      if (this.foldersFullAccess) {
        scopes.push('folders:full');
      } else {
        if (this.foldersRead) scopes.push('folders:read');
        if (this.foldersWrite) scopes.push('folders:write');
      }

      if (this.linksFullAccess) {
        scopes.push('links:full');
      } else {
        if (this.linksRead) scopes.push('links:read');
        if (this.linksWrite) scopes.push('links:write');
      }

      return scopes;
    },
    isSubmitDisabled() {
      return this.selectedUserHasClient || this.selectedScopes.length === 0;
    },
    accessPointId: function() {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
  },
}
</script>
